import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const StudentLeave = ({ onNavigate, userInfo }) => {
    // Layout State
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    // Page Specific State
    const [activeTab, setActiveTab] = useState('apply'); // 'apply', 'history', 'calendar'
    const [loading, setLoading] = useState(false);
    const [leaveApplications, setLeaveApplications] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // Holiday popup state
    const [selectedHoliday, setSelectedHoliday] = useState(null);

    // Form state for leave application
    const [leaveForm, setLeaveForm] = useState({
        leaveType: '',
        fromDate: '',
        toDate: '',
        reason: '',
        attachment: null
    });

    // Leave types
    const leaveTypes = ['Sick Leave', 'Personal', 'Medical', 'Emergency', 'Other'];

    // Calendar state (for Holiday Calendar tab)
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // Mock Holiday Calendar Data
    const holidays = [
        { date: '2026-01-26', name: 'Republic Day', type: 'National Holiday' },
        { date: '2026-03-08', name: 'Maha Shivaratri', type: 'Religious Holiday' },
        { date: '2026-03-25', name: 'Holi', type: 'Religious Holiday' },
        { date: '2026-03-29', name: 'Good Friday', type: 'Religious Holiday' },
        { date: '2026-04-11', name: 'Eid-ul-Fitr', type: 'Religious Holiday' },
        { date: '2026-04-14', name: 'Dr. Ambedkar Jayanti', type: 'National Holiday' },
        { date: '2026-08-15', name: 'Independence Day', type: 'National Holiday' },
        { date: '2026-10-02', name: 'Gandhi Jayanti', type: 'National Holiday' },
        { date: '2026-10-24', name: 'Diwali', type: 'Religious Holiday' },
        { date: '2026-12-25', name: 'Christmas', type: 'Religious Holiday' },
    ];

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);



    // Fetch leave applications on mount
    useEffect(() => {
        fetchLeaveApplications();
    }, []);



    const fetchLeaveApplications = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            if (!token) {
                loadDemoLeaveApplications();
                return;
            }

            const response = await fetch(`${API_LINK}/api/student/leave-applications`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    setLeaveApplications(result.data);
                }
            } else {
                loadDemoLeaveApplications();
            }
        } catch (error) {
            console.error('Error fetching leave applications:', error);
            loadDemoLeaveApplications();
        }
    };

    const loadDemoLeaveApplications = () => {
        const demoApplications = [
            {
                id: 1,
                leaveType: 'Sick Leave',
                fromDate: '2026-01-20',
                toDate: '2026-01-22',
                reason: 'Suffering from fever',
                status: 'Pending',
                appliedOn: '2026-01-17',
                attachment: null
            },
            {
                id: 2,
                leaveType: 'Personal',
                fromDate: '2026-01-10',
                toDate: '2026-01-11',
                reason: 'Family function',
                status: 'Approved',
                appliedOn: '2026-01-08',
                approvedOn: '2026-01-09',
                attachment: null
            },
            {
                id: 3,
                leaveType: 'Medical',
                fromDate: '2026-01-05',
                toDate: '2026-01-06',
                reason: 'Doctor appointment',
                status: 'Rejected',
                appliedOn: '2026-01-03',
                rejectedOn: '2026-01-04',
                attachment: null
            }
        ];
        setLeaveApplications(demoApplications);
    };



    // Handle form input change
    const handleInputChange = (field, value) => {
        setLeaveForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle file upload
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrorMessage('❌ File size should not exceed 5MB');
                setTimeout(() => setErrorMessage(null), 3000);
                return;
            }
            setLeaveForm(prev => ({
                ...prev,
                attachment: file
            }));
        }
    };

    // Remove attachment
    const removeAttachment = () => {
        setLeaveForm(prev => ({
            ...prev,
            attachment: null
        }));
    };

    // Submit leave application
    const handleSubmitLeave = async (e) => {
        e.preventDefault();

        // Validate dates
        if (new Date(leaveForm.fromDate) > new Date(leaveForm.toDate)) {
            setErrorMessage('❌ "From Date" cannot be after "To Date"');
            setTimeout(() => setErrorMessage(null), 3000);
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            if (!token) {
                // Demo mode - just add to local state
                const newApplication = {
                    id: leaveApplications.length + 1,
                    ...leaveForm,
                    status: 'Pending',
                    appliedOn: new Date().toISOString().split('T')[0]
                };
                setLeaveApplications([newApplication, ...leaveApplications]);
                setSuccessMessage('✅ Leave application submitted successfully!');
                setLeaveForm({
                    leaveType: '',
                    fromDate: '',
                    toDate: '',
                    reason: '',
                    attachment: null
                });
                setActiveTab('history');
                setTimeout(() => setSuccessMessage(null), 3000);
                return;
            }

            const formData = new FormData();
            formData.append('leaveType', leaveForm.leaveType);
            formData.append('fromDate', leaveForm.fromDate);
            formData.append('toDate', leaveForm.toDate);
            formData.append('reason', leaveForm.reason);
            if (leaveForm.attachment) {
                formData.append('attachment', leaveForm.attachment);
            }

            const response = await fetch(`${API_LINK}/api/student/leave-applications/submit`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                setSuccessMessage('✅ Leave application submitted successfully!');
                fetchLeaveApplications();
                setLeaveForm({
                    leaveType: '',
                    fromDate: '',
                    toDate: '',
                    reason: '',
                    attachment: null
                });
                setActiveTab('history');
                setTimeout(() => setSuccessMessage(null), 3000);
            } else {
                setErrorMessage('❌ Failed to submit leave application');
                setTimeout(() => setErrorMessage(null), 3000);
            }
        } catch (error) {
            console.error('Error submitting leave:', error);
            setErrorMessage('❌ Failed to submit leave application');
            setTimeout(() => setErrorMessage(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved':
                return { bg: '#d4edda', border: '#c3e6cb', text: '#155724' };
            case 'Rejected':
                return { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' };
            default:
                return { bg: '#fff3cd', border: '#ffeaa7', text: '#856404' };
        }
    };

    const calculateDays = (from, to) => {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        const diffTime = Math.abs(toDate - fromDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    };

    return (
        <Layout role="student"
            onNavigate={onNavigate}
            activePage="student-leave"
            pageTitle="Leave & Holidays"
        >
            {/* Success/Error Messages */}
            {(successMessage || errorMessage) && (
                <div style={{
                    background: successMessage ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${successMessage ? '#c3e6cb' : '#f5c6cb'}`,
                    color: successMessage ? '#155724' : '#721c24',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span>{successMessage || errorMessage}</span>
                    <button
                        onClick={() => {
                            setSuccessMessage(null);
                            setErrorMessage(null);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Header Card */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                overflow: 'hidden'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    padding: '2rem',
                    textAlign: 'center'
                }}>
                    <h1 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '2rem',
                        fontWeight: 'bold'
                    }}>
                        🏖️ Leave Application & Holiday Calendar
                    </h1>
                    <p style={{
                        margin: 0,
                        fontSize: '1.1rem',
                        opacity: 0.9
                    }}>
                        Apply for leave and view academic holidays
                    </p>
                </div>

                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    borderBottom: '1px solid #ecf0f1'
                }}>
                    {[
                        { id: 'apply', label: 'Apply for Leave', icon: '➕' },
                        { id: 'history', label: 'Leave History', icon: '📋' },
                        { id: 'calendar', label: 'Holiday Calendar', icon: '📅' }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1,
                                background: activeTab === tab.id ? '#238548' : 'transparent',
                                color: activeTab === tab.id ? '#ffffff' : '#666',
                                border: 'none',
                                padding: '1rem',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: '500',
                                transition: 'all 0.3s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <span>{tab.icon}</span>
                            <span style={{ display: isMobile ? 'none' : 'inline' }}>{tab.label}</span>
                            <span style={{ display: isMobile ? 'inline' : 'none' }}>{tab.label.split(' ')[0]}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'apply' && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    padding: '2rem'
                }}>
                    <h2 style={{
                        margin: '0 0 1.5rem 0',
                        color: '#238548',
                        fontSize: '1.5rem'
                    }}>
                        ➕ Apply for Leave
                    </h2>

                    <form onSubmit={handleSubmitLeave}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: '1.5rem',
                            marginBottom: '1.5rem'
                        }}>
                            {/* Leave Type */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#333'
                                }}>
                                    Leave Type <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <select
                                    value={leaveForm.leaveType}
                                    onChange={(e) => handleInputChange('leaveType', e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none'
                                    }}
                                >
                                    <option value="">Select Leave Type</option>
                                    {leaveTypes.map((type, index) => (
                                        <option key={index} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {/* From Date */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#333'
                                }}>
                                    From Date <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={leaveForm.fromDate}
                                    onChange={(e) => handleInputChange('fromDate', e.target.value)}
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            {/* To Date */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#333'
                                }}>
                                    To Date <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <input
                                    type="date"
                                    value={leaveForm.toDate}
                                    onChange={(e) => handleInputChange('toDate', e.target.value)}
                                    required
                                    min={leaveForm.fromDate || new Date().toISOString().split('T')[0]}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>

                            {/* Days Count Display */}
                            {leaveForm.fromDate && leaveForm.toDate && (
                                <div style={{
                                    gridColumn: isMobile ? 'auto' : 'span 2',
                                    background: '#e8f5e9',
                                    padding: '1rem',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <strong>Total Days:</strong> {calculateDays(leaveForm.fromDate, leaveForm.toDate)} day(s)
                                </div>
                            )}

                            {/* Reason */}
                            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#333'
                                }}>
                                    Reason for Leave <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <textarea
                                    value={leaveForm.reason}
                                    onChange={(e) => handleInputChange('reason', e.target.value)}
                                    placeholder="Please provide a detailed reason for your leave"
                                    required
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Attachment */}
                            <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '500',
                                    color: '#333'
                                }}>
                                    Attachment (Optional)
                                </label>
                                <p style={{
                                    fontSize: '0.85rem',
                                    color: '#666',
                                    margin: '0 0 0.5rem 0'
                                }}>
                                    Upload medical certificate or supporting documents (Max 5MB, PDF/Image)
                                </p>
                                <input
                                    type="file"
                                    onChange={handleFileUpload}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px solid #ecf0f1',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        outline: 'none'
                                    }}
                                />
                                {leaveForm.attachment && (
                                    <div style={{
                                        marginTop: '0.5rem',
                                        padding: '0.75rem',
                                        background: '#f8f9fa',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <span style={{ fontSize: '0.9rem', color: '#333' }}>
                                            📎 {leaveForm.attachment.name}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={removeAttachment}
                                            style={{
                                                background: '#dc3545',
                                                color: '#fff',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setLeaveForm({
                                        leaveType: '',
                                        fromDate: '',
                                        toDate: '',
                                        reason: '',
                                        attachment: null
                                    });
                                }}
                                style={{
                                    background: '#6c757d',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '1rem 2rem',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    transition: 'all 0.3s'
                                }}
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    background: loading ? '#97d6ac' : '#238548',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '1rem 2rem',
                                    borderRadius: '8px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    transition: 'all 0.3s'
                                }}
                            >
                                {loading ? 'Submitting...' : 'Submit Leave Application'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'history' && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    padding: '2rem'
                }}>
                    <h2 style={{
                        margin: '0 0 1.5rem 0',
                        color: '#238548',
                        fontSize: '1.5rem'
                    }}>
                        📋 Leave History
                    </h2>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {leaveApplications.length > 0 ? (
                            leaveApplications.map(leave => {
                                const statusColors = getStatusColor(leave.status);
                                return (
                                    <div key={leave.id} style={{
                                        border: `2px solid ${statusColors.border}`,
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        background: statusColors.bg,
                                        transition: 'transform 0.3s'
                                    }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <div>
                                                <h3 style={{
                                                    margin: '0 0 0.5rem 0',
                                                    color: '#333',
                                                    fontSize: '1.2rem'
                                                }}>
                                                    {leave.leaveType}
                                                </h3>
                                                <p style={{
                                                    margin: '0 0 0.25rem 0',
                                                    color: '#555',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    <strong>Duration:</strong> {formatDate(leave.fromDate)} to {formatDate(leave.toDate)}
                                                </p>
                                                <p style={{
                                                    margin: '0 0 0.25rem 0',
                                                    color: '#555',
                                                    fontSize: '0.95rem'
                                                }}>
                                                    <strong>Applied On:</strong> {formatDate(leave.appliedOn)}
                                                </p>
                                            </div>
                                            <div>
                                                <span style={{
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '600',
                                                    color: statusColors.text,
                                                    backgroundColor: '#ffffff',
                                                    border: `2px solid ${statusColors.border}`,
                                                    display: 'inline-block'
                                                }}>
                                                    {leave.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{
                                            padding: '1rem',
                                            background: '#ffffff',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <p style={{
                                                margin: '0',
                                                color: '#555',
                                                fontSize: '0.95rem'
                                            }}>
                                                <strong>Reason:</strong> {leave.reason}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div style={{
                                padding: '3rem',
                                textAlign: 'center',
                                color: '#666'
                            }}>
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                                <p style={{ fontSize: '1.1rem', margin: 0 }}>No leave applications found.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'calendar' && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    padding: '2rem'
                }}>
                    <h2 style={{
                        margin: '0 0 1.5rem 0',
                        color: '#238548',
                        fontSize: '1.5rem',
                        borderBottom: '2px solid #f0f0f0',
                        paddingBottom: '1rem'
                    }}>
                        📅 Academic Holiday Calendar 2026
                    </h2>

                    {/* Calendar Component */}
                    {(() => {
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'];
                        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

                        // Get first day of month and total days
                        const firstDay = new Date(currentYear, currentMonth, 1).getDay();
                        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

                        // Create calendar array
                        const calendarDays = [];
                        for (let i = 0; i < firstDay; i++) {
                            calendarDays.push(null); // Empty cells before month starts
                        }
                        for (let day = 1; day <= daysInMonth; day++) {
                            calendarDays.push(day);
                        }

                        // Check if a date is a holiday
                        const isHoliday = (day) => {
                            if (!day) return false;
                            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            return holidays.some(h => h.date === dateStr);
                        };

                        // Get holiday for a specific date
                        const getHoliday = (day) => {
                            if (!day) return null;
                            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            return holidays.find(h => h.date === dateStr);
                        };

                        // Navigate months
                        const prevMonth = () => {
                            if (currentMonth === 0) {
                                setCurrentMonth(11);
                                setCurrentYear(currentYear - 1);
                            } else {
                                setCurrentMonth(currentMonth - 1);
                            }
                        };

                        const nextMonth = () => {
                            if (currentMonth === 11) {
                                setCurrentMonth(0);
                                setCurrentYear(currentYear + 1);
                            } else {
                                setCurrentMonth(currentMonth + 1);
                            }
                        };

                        return (
                            <div style={{
                                maxWidth: isMobile ? '100%' : '800px',
                                margin: '0 auto'
                            }}>
                                {/* Calendar Header with Month Navigation */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1.5rem',
                                    padding: isMobile ? '1rem' : '0.75rem 1rem',
                                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                    borderRadius: '12px',
                                    color: '#fff'
                                }}>
                                    <button
                                        onClick={prevMonth}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            color: '#fff',
                                            fontSize: isMobile ? '1.2rem' : '1rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                        onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                                    >
                                        ◀
                                    </button>
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '1.5rem' : '1.3rem', fontWeight: 'bold' }}>
                                        {monthNames[currentMonth]} {currentYear}
                                    </h3>
                                    <button
                                        onClick={nextMonth}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            color: '#fff',
                                            fontSize: isMobile ? '1.2rem' : '1rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                        onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                                    >
                                        ▶
                                    </button>
                                </div>

                                {/* Calendar Grid */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: isMobile ? '0.5rem' : '0.35rem',
                                    marginBottom: '2rem'
                                }}>
                                    {/* Day Headers */}
                                    {dayNames.map((day, index) => (
                                        <div key={index} style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            padding: isMobile ? '0.75rem' : '0.5rem',
                                            background: '#f0f0f0',
                                            borderRadius: '8px',
                                            color: '#333',
                                            fontSize: isMobile ? '0.8rem' : '0.85rem'
                                        }}>
                                            {isMobile ? day.substring(0, 1) : day}
                                        </div>
                                    ))}

                                    {/* Calendar Days */}
                                    {calendarDays.map((day, index) => {
                                        const holidayInfo = getHoliday(day);
                                        const isToday = day &&
                                            currentYear === new Date().getFullYear() &&
                                            currentMonth === new Date().getMonth() &&
                                            day === new Date().getDate();

                                        return (
                                            <div
                                                key={index}
                                                title={holidayInfo ? holidayInfo.name : ''}
                                                style={{
                                                    aspectRatio: '1',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: isMobile ? '0.5rem' : '0.35rem',
                                                    borderRadius: '8px',
                                                    border: isToday ? '2px solid #238548' : '1px solid #e0e0e0',
                                                    background: day ? (isHoliday(day) ? '#ffebee' : '#ffffff') : 'transparent',
                                                    color: day ? (isHoliday(day) ? '#c62828' : '#333') : 'transparent',
                                                    fontWeight: isHoliday(day) ? 'bold' : 'normal',
                                                    fontSize: isMobile ? '0.9rem' : '0.85rem',
                                                    position: 'relative',
                                                    cursor: holidayInfo ? 'pointer' : 'default',
                                                    transition: 'all 0.3s'
                                                }}
                                                onClick={() => {
                                                    if (holidayInfo) {
                                                        setSelectedHoliday(holidayInfo);
                                                    }
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (holidayInfo) {
                                                        e.currentTarget.style.transform = 'scale(1.1)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(198, 40, 40, 0.3)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (holidayInfo) {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                {day && (
                                                    <>
                                                        <span>{day}</span>
                                                        {isHoliday(day) && (
                                                            <span style={{
                                                                fontSize: '0.6rem',
                                                                position: 'absolute',
                                                                bottom: '2px',
                                                                background: '#c62828',
                                                                color: '#fff',
                                                                borderRadius: '50%',
                                                                width: '6px',
                                                                height: '6px'
                                                            }}>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Holiday List Below Calendar */}
                                <div style={{
                                    borderTop: '2px solid #f0f0f0',
                                    paddingTop: '2rem'
                                }}>
                                    <h3 style={{
                                        margin: '0 0 1.5rem 0',
                                        color: '#238548',
                                        fontSize: '1.3rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        🎉 All Holidays for {currentYear}
                                    </h3>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                                        gap: '1rem'
                                    }}>
                                        {holidays
                                            .filter(h => new Date(h.date).getFullYear() === currentYear)
                                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                                            .map((holiday, index) => {
                                                const holidayDate = new Date(holiday.date);
                                                const monthName = monthNames[holidayDate.getMonth()];
                                                const isPassed = new Date() > holidayDate;

                                                return (
                                                    <div key={index} style={{
                                                        border: '2px solid #e0e0e0',
                                                        borderRadius: '12px',
                                                        padding: '1rem',
                                                        display: 'flex',
                                                        gap: '1rem',
                                                        alignItems: 'center',
                                                        background: isPassed ? '#f5f5f5' : 'linear-gradient(to right, #fff5f5, #ffffff)',
                                                        borderLeftColor: '#c62828',
                                                        borderLeftWidth: '4px',
                                                        transition: 'all 0.3s',
                                                        opacity: isPassed ? 0.7 : 1
                                                    }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateX(5px)';
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateX(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{
                                                            background: isPassed ? '#9e9e9e' : '#c62828',
                                                            color: '#fff',
                                                            borderRadius: '8px',
                                                            padding: '0.75rem',
                                                            textAlign: 'center',
                                                            minWidth: '60px',
                                                            flexShrink: 0
                                                        }}>
                                                            <div style={{
                                                                fontSize: '1.5rem',
                                                                fontWeight: 'bold',
                                                                lineHeight: 1
                                                            }}>
                                                                {holidayDate.getDate()}
                                                            </div>
                                                            <div style={{
                                                                fontSize: '0.75rem',
                                                                marginTop: '0.25rem',
                                                                opacity: 0.9
                                                            }}>
                                                                {monthName.substring(0, 3)}
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <h4 style={{
                                                                margin: '0 0 0.25rem 0',
                                                                color: '#333',
                                                                fontSize: '1rem',
                                                                fontWeight: '600'
                                                            }}>
                                                                {holiday.name}
                                                            </h4>
                                                            <div style={{
                                                                fontSize: '0.85rem',
                                                                color: '#666',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                flexWrap: 'wrap'
                                                            }}>
                                                                <span style={{
                                                                    background: '#e0f2f1',
                                                                    color: '#00695c',
                                                                    padding: '0.25rem 0.75rem',
                                                                    borderRadius: '12px',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {holiday.type}
                                                                </span>
                                                                {isPassed && (
                                                                    <span style={{
                                                                        background: '#e0e0e0',
                                                                        color: '#666',
                                                                        padding: '0.25rem 0.75rem',
                                                                        borderRadius: '12px',
                                                                        fontSize: '0.75rem'
                                                                    }}>
                                                                        Past
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Holiday Details Popup Modal */}
            {selectedHoliday && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        animation: 'fadeIn 0.3s ease'
                    }}
                    onClick={() => setSelectedHoliday(null)}
                >
                    <div
                        style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '2rem',
                            maxWidth: '400px',
                            width: '90%',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                            animation: 'slideUp 0.3s ease',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={() => setSelectedHoliday(null)}
                            style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '50%',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.2rem',
                                color: '#666',
                                transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#e0e0e0';
                                e.target.style.color = '#333';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f0f0f0';
                                e.target.style.color = '#666';
                            }}
                        >
                            ✕
                        </button>

                        {/* Holiday Icon */}
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1rem',
                                fontSize: '2.5rem',
                                boxShadow: '0 4px 15px rgba(238, 90, 90, 0.4)'
                            }}>
                                🎉
                            </div>
                            <h2 style={{
                                margin: '0 0 0.5rem 0',
                                color: '#333',
                                fontSize: '1.5rem',
                                fontWeight: '600'
                            }}>
                                {selectedHoliday.name}
                            </h2>
                        </div>

                        {/* Holiday Details */}
                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '12px',
                            padding: '1.25rem',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                marginBottom: '1rem'
                            }}>
                                <span style={{
                                    background: '#238548',
                                    color: '#fff',
                                    padding: '0.5rem',
                                    borderRadius: '8px',
                                    fontSize: '1.2rem'
                                }}>📅</span>
                                <div>
                                    <div style={{
                                        fontSize: '0.85rem',
                                        color: '#666',
                                        marginBottom: '0.25rem'
                                    }}>Date</div>
                                    <div style={{
                                        fontWeight: '600',
                                        color: '#333',
                                        fontSize: '1rem'
                                    }}>
                                        {new Date(selectedHoliday.date).toLocaleDateString('en-GB', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}>
                                <span style={{
                                    background: selectedHoliday.type === 'National Holiday' ? '#1976d2' : '#e91e63',
                                    color: '#fff',
                                    padding: '0.5rem',
                                    borderRadius: '8px',
                                    fontSize: '1.2rem'
                                }}>📌</span>
                                <div>
                                    <div style={{
                                        fontSize: '0.85rem',
                                        color: '#666',
                                        marginBottom: '0.25rem'
                                    }}>Type</div>
                                    <div style={{
                                        fontWeight: '600',
                                        color: selectedHoliday.type === 'National Holiday' ? '#1976d2' : '#e91e63',
                                        fontSize: '1rem'
                                    }}>
                                        {selectedHoliday.type}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => setSelectedHoliday(null)}
                            style={{
                                width: '100%',
                                background: 'linear-gradient(135deg, #238548 0%, #2ea35a 100%)',
                                color: '#fff',
                                border: 'none',
                                padding: '1rem',
                                borderRadius: '10px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s',
                                boxShadow: '0 4px 15px rgba(35, 133, 72, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 20px rgba(35, 133, 72, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 15px rgba(35, 133, 72, 0.3)';
                            }}
                        >
                            Got it! 👍
                        </button>
                    </div>
                </div>
            )}

            {/* CSS Animations */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </Layout>
    );
};

export default StudentLeave;